import type { MaskGroup, MaskLabel, MaskSchema, MaskSeriesFilter } from "@frontend/pages/case-workspace/radiology/types"

/**
 * Always returns groups — wraps a flat `labels` array in a single unnamed
 * default group when the schema uses the legacy flat format.
 */
export function normalizeMaskGroups(schema: MaskSchema): MaskGroup[] {
  if (schema.groups?.length) return schema.groups
  return [{ key: "__default__", name: "", labels: schema.labels ?? [] }]
}

/**
 * Flattens groups (or legacy flat labels) to a single label array.
 * Use wherever the old `schema.labels` was referenced.
 */
export function flattenMaskLabels(schema: MaskSchema): MaskLabel[] {
  if (schema.groups?.length) return schema.groups.flatMap((g) => g.labels)
  return schema.labels ?? []
}

/**
 * Check whether a series satisfies the mask module's optional series filter.
 * Returns `true` when no filter is defined or when all present criteria match.
 * Criteria are AND-ed across fields; values within `image_plane` / `series_description` are OR-ed.
 */
export function seriesMatchesMaskFilter(
  series: { image_plane?: string | null; series_description?: string | null; number_of_frames?: number | null } | null,
  filter?: MaskSeriesFilter
): boolean {
  if (!filter) return true
  if (!series) return false

  const hasAnyRule =
    (filter.image_plane && filter.image_plane.length > 0) ||
    (filter.series_description && filter.series_description.length > 0) ||
    filter.number_of_frames != null
  if (!hasAnyRule) return true

  // image_plane: series value must be one of the allowed planes (case-insensitive)
  if (filter.image_plane && filter.image_plane.length > 0) {
    const plane = series.image_plane?.toLowerCase() ?? ""
    if (!filter.image_plane.some((p) => p.toLowerCase() === plane)) return false
  }

  // series_description: at least one substring must appear (case-insensitive)
  if (filter.series_description && filter.series_description.length > 0) {
    const desc = series.series_description?.toLowerCase() ?? ""
    if (!(desc && filter.series_description.some((sub) => desc.includes(sub.toLowerCase())))) return false
  }

  // number_of_frames: comparison operator
  if (filter.number_of_frames != null) {
    const n = series.number_of_frames
    if (n == null) return false
    const { op, value } = filter.number_of_frames
    if (op === "gte" && !(n >= value)) return false
    if (op === "eq" && n !== value) return false
    if (op === "lte" && !(n <= value)) return false
  }

  return true
}

/**
 * Build a human-readable reason string when a series doesn't match the filter.
 */
export function describeMaskFilterMismatch(filter?: MaskSeriesFilter): string | null {
  if (!filter) return null
  const parts: string[] = []
  if (filter.image_plane?.length) {
    parts.push(`plane: ${filter.image_plane.join(" or ")}`)
  }
  if (filter.series_description?.length) {
    parts.push(`description contains: ${filter.series_description.map((s) => `"${s}"`).join(" or ")}`)
  }
  if (filter.number_of_frames) {
    const { op, value } = filter.number_of_frames
    const opLabel = op === "gte" ? "≥" : op === "lte" ? "≤" : "="
    parts.push(`frames ${opLabel} ${value}`)
  }
  return parts.length > 0 ? `Requires ${parts.join(", ")}` : null
}
